import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Check, ShieldCheck } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function SmsOptIn() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Please check the box to agree to receive texts.");
      return;
    }
    if (!agreeTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy.");
      return;
    }
    if (!phone.trim()) {
      setError("Please enter your mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch(
        "/sms/opt-in",
        { method: "POST", body: JSON.stringify({ name: name.trim(), phone: phone.trim(), consent, agree_terms: agreeTerms }) },
        false
      );
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-[#FCFBF8]/85 border-b border-[#E8E3DA]">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size={32} to="/" />
          <span className="font-display font-extrabold text-sm">Text alerts</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-8" data-testid="sms-optin-screen">
        <div className="h-14 w-14 rounded-3xl bg-[#F6DED5] flex items-center justify-center mb-4">
          <MessageSquare size={26} color="#E86A45" />
        </div>
        <h1 className="font-display text-3xl font-black leading-tight">Get Wooflect text alerts</h1>
        <p className="text-[#4A4746] mt-3 leading-relaxed">
          Sign up to receive <b>vaccine &amp; vet-visit reminders</b>, <b>pet-health
          emergency alerts</b>, and important <b>account notifications</b> from
          Wooflect by Happy Big Dogs — right to your phone.
        </p>

        {done ? (
          <div
            className="mt-8 rounded-3xl bg-[#E8F0E5] p-6 text-center"
            data-testid="sms-optin-success"
          >
            <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center mx-auto mb-3">
              <Check size={28} color="#6F8F72" />
            </div>
            <h2 className="font-display text-xl font-extrabold">You&apos;re subscribed! 🐾</h2>
            <p className="text-[#4A4746] mt-2 text-sm">
              We just sent a confirmation text to <b>{phone}</b>. Reply <b>STOP</b> any
              time to unsubscribe, or <b>HELP</b> for help.
            </p>
            <Link to="/" className="inline-block mt-5 rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-2.5">
              Back to Wooflect
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7">
            <label className="block text-sm font-bold mb-1.5">Your name (optional)</label>
            <input
              data-testid="sms-optin-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full rounded-2xl border border-[#E8E3DA] bg-white px-4 py-3 outline-none focus:border-[#E86A45] mb-4"
            />

            <label className="block text-sm font-bold mb-1.5">Mobile phone number</label>
            <input
              data-testid="sms-optin-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              className="w-full rounded-2xl border border-[#E8E3DA] bg-white px-4 py-3 outline-none focus:border-[#E86A45]"
            />

            {/* Consent — intentionally NOT pre-checked */}
            <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
              <input
                data-testid="sms-optin-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 accent-[#6F8F72]"
              />
              <span className="text-sm text-[#4A4746] leading-relaxed">
                I agree to receive recurring automated texts from Wooflect by Happy Big
                Dogs (vaccine/vet reminders, emergency alerts, account notices). Consent
                isn&apos;t a condition of purchase. Msg frequency varies. Msg &amp; data
                rates may apply. Reply HELP for help, STOP to cancel.
              </span>
            </label>

            <label className="flex items-start gap-3 mt-3 cursor-pointer select-none">
              <input
                data-testid="sms-optin-terms"
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 accent-[#6F8F72]"
              />
              <span className="text-sm text-[#4A4746] leading-relaxed">
                I have read and agree to the{" "}
                <Link to="/legal/terms" className="font-bold text-[#E86A45] underline">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/legal/privacy" className="font-bold text-[#E86A45] underline">Privacy Policy</Link>.
              </span>
            </label>

            {error && (
              <p className="text-sm text-[#D9534F] font-bold mt-3" data-testid="sms-optin-error">{error}</p>
            )}

            <button
              type="submit"
              data-testid="sms-optin-submit"
              disabled={submitting}
              className="w-full mt-6 rounded-full bg-[#E86A45] text-white font-extrabold py-3.5 hover:bg-[#d65f3c] transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing you up…" : "Yes, sign me up!"}
            </button>

            {/* Disclaimers */}
            <div className="mt-6 rounded-2xl bg-[#F2EFE9] p-4 text-xs text-[#6B6560] leading-relaxed space-y-1.5">
              <p className="flex items-center gap-1.5 font-bold text-[#4A4746]">
                <ShieldCheck size={14} color="#6F8F72" /> Your privacy is protected
              </p>
              <p>
                <b>Message frequency varies.</b> Message and data rates may apply.
              </p>
              <p>
                Reply <b>HELP</b> for help or <b>STOP</b> to cancel at any time.
              </p>
              <p>
                No mobile information will be sold or shared with third parties or affiliates
                for marketing purposes.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
